import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Box,
  Card,
  useTheme,
} from "@mui/material";
import { auth, getFirebaseErrorMessage } from "../../../firebase/firebase";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { createUserWithEmailAndPassword } from "firebase/auth";
import SpinningCustomLogo from "../../Common/SpinningCustomLogo";

export const SignUpForm = ({
  strings,
  defaultEmail = "",
  emailDisabled,
  subscription,
  sessionId,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPasswordError, setConfirmPasswordError] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState(defaultEmail);

  useEffect(() => {
    if (defaultEmail) setEmail(defaultEmail);
  }, [defaultEmail]);

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setConfirmPasswordError(true);
      return null;
    }
    setLoading(true);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      // Navigate or handle success state here
    } catch (error) {
      setError(getFirebaseErrorMessage(error?.code));
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = "#1976d2";

  return (
    <Card
      elevation={0}
      sx={{
        display: "flex",
        maxWidth: "1040px",
        width: "100%",
        borderRadius: "28px",
        overflow: "hidden",
        boxShadow: "0 10px 40px -10px rgba(0,0,0,0.1)",
        border: "none",
      }}
    >
      <Grid container>
        {/* Left Column: Branding / Marketing */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            background: "linear-gradient(180deg, #f8f9fa 0%, #f1f3f4 100%)",
            p: 6,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            borderRight: "1px solid rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              maxWidth: "350px",
              width: "100%",
            }}
          >
            <Box
              component="img"
              src="/ct_logo_black.png"
              alt="CandidateTools logo"
              sx={{ width: "180px", height: "auto", mb: 3 }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />

            {sessionId && subscription ? (
              <Box>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{ fontWeight: 700, mb: 1, color: "#202124" }}
                >
                  {strings.subscribedHeader}
                </Typography>
                <Typography
                  variant="h3"
                  component="p"
                  sx={{ fontWeight: "bold", color: primaryColor, mb: 2 }}
                >
                  ${subscription.price}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "1rem",
                      fontWeight: "normal",
                      ml: 1,
                      color: "#5f6368",
                    }}
                  >
                    / month
                  </Typography>
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#5f6368", lineHeight: 1.6 }}
                >
                  You've successfully subscribed! Just create a password to
                  finish setting up your account.
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{ fontWeight: 700, mb: 2, color: "#202124" }}
                >
                  {strings.signUpHeader}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "#5f6368", lineHeight: 1.6 }}
                >
                  {strings.signUpSubtitle}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Right Column: Auth Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            bgcolor: "white",
            p: { xs: 4, sm: 6 },
            display: "flex",
            alignItems: "center",
            borderLeft: "1px solid rgba(0,0,0,0.05)",
          }}
        >
          <Box
            component="form"
            onSubmit={handleSignUp}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              width: "100%",
            }}
          >
            {/* Form Fields */}
            <TextField
              label={strings.emailLabel}
              type="email"
              fullWidth
              required
              variant="outlined"
              value={email}
              // If email is provided by session/url, we can disable edits or just pre-fill
              // disabled={emailDisabled}
              onChange={(e) => !emailDisabled && setEmail(e.target.value)}
              InputLabelProps={{ shrink: !!email || undefined }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  backgroundColor: "#fff",
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                    borderWidth: 2,
                  },
                },
                "& .MuiInputLabel-root": { color: "#5f6368" },
              }}
            />

            <TextField
              label={strings.passwordLabel}
              type={showPassword ? "text" : "password"}
              fullWidth
              required
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  backgroundColor: "#fff",
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                    borderWidth: 2,
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              label={strings.confirmPasswordLabel}
              type={showConfirmPassword ? "text" : "password"}
              fullWidth
              required
              variant="outlined"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setConfirmPasswordError(e.target.value !== password);
              }}
              error={confirmPasswordError}
              helperText={
                confirmPasswordError ? strings.passwordsMustMatch : ""
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  backgroundColor: "#fff",
                  "&.Mui-focused fieldset": {
                    borderColor: primaryColor,
                    borderWidth: 2,
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      edge="end"
                      size="small"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {error && (
              <Alert severity="error" sx={{ borderRadius: 1.5 }}>
                {error}
              </Alert>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || confirmPasswordError}
              sx={{
                bgcolor: primaryColor,
                "&:hover": {
                  bgcolor: "#1565c0",
                  boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                },
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 1.5,
                boxShadow: "none",
                fontSize: "1rem",
              }}
            >
              {loading ? (
                <SpinningCustomLogo size={24} color="inherit" />
              ) : (
                strings.submitButton
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
};
